import { useEffect, useRef, useState } from "react";

const DEFAULT_DURATION_MS = 600;

export function useCountUp(target: number | null, durationMs: number = DEFAULT_DURATION_MS): number | null {
  const [displayed, setDisplayed] = useState<number | null>(target);
  const rafRef = useRef<number | null>(null);
  const prevTargetRef = useRef<number | null>(target);

  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    if (target === null) {
      setDisplayed(null);
      prevTargetRef.current = null;
      return;
    }

    const from = prevTargetRef.current ?? 0;
    const to = target;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevTargetRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return displayed;
}
